/**
 * Estratégias puras de recovery por cenário.
 */

import type { QueueItem } from '../queue/types';
import { recoverAfterDeadLeader } from '../coordination/distributedRecovery';
import {
  planReplayRecovery,
  consumeReplayRecoveryScheduled,
} from '../replay/replayRecovery';
import { reconcileAllSessionCursors } from '../replay/replayCursorReconciliation';
import { compactQueueItems, dedupeHeartbeatInBuffer } from '../queue/compaction';
import { evictLowPriorityItems } from '../storage/quotaManager';
import { quarantineGeneration } from '../storage/storageOrchestrator';
import {
  rebuildQueueIndexFromArray,
  validateQueueIndex,
} from '../queue/queueIndex';
import { isQueueIndexEnabled } from '../queue/queueIndex';
import {
  getActiveReplaySession,
  failReplaySession,
  assertReplaySessionValid,
} from '../replay/replaySession';
import { scheduleReplayRecovery } from '../replay/replayRecovery';
import { _testClearReplaySnapshots } from '../replay/replaySnapshot';

let recoverySnapshot: QueueItem[] | null = null;

export function captureRecoverySnapshot(queue: QueueItem[]): QueueItem[] {
  recoverySnapshot = queue.map(i => ({ ...i, params: { ...i.params } }));
  return recoverySnapshot;
}

export function rollbackRecoverySnapshot(
  _snapshot: QueueItem[],
  fallback: QueueItem[]
): QueueItem[] {
  const restored = recoverySnapshot
    ? recoverySnapshot.map(i => ({ ...i, params: { ...i.params } }))
    : fallback;
  recoverySnapshot = null;
  return restored;
}

export function recoverStaleLeader(
  queue: QueueItem[],
  tabId: string,
  aliveTabIds: Set<string>
): QueueItem[] {
  return recoverAfterDeadLeader(queue, tabId, aliveTabIds);
}

export function recoverInterruptedReplay(items: QueueItem[]): QueueItem[] {
  if (consumeReplayRecoveryScheduled()) {
    return planReplayRecovery(items).items;
  }
  return planReplayRecovery(items).items;
}

export function recoverCorruptedGeneration(generation: number): void {
  quarantineGeneration(generation);
}

export function recoverReplayCursorInvalid(items: QueueItem[]): QueueItem[] {
  const results = reconcileAllSessionCursors(items);
  if (results.some(r => r.action === 'recovery')) {
    return planReplayRecovery(items).items;
  }
  return items;
}

export function recoverInvalidIndex(queue: QueueItem[]): void {
  if (!isQueueIndexEnabled()) return;
  const v = validateQueueIndex(queue);
  if (!v.ok) rebuildQueueIndexFromArray(queue);
}

export function recoverPartitionOverflow(
  items: QueueItem[],
  maxItems: number
): QueueItem[] {
  if (items.length <= maxItems) return items;
  return evictLowPriorityItems(items, maxItems);
}

export function recoverFailedShadowCommit(items: QueueItem[]): QueueItem[] {
  return compactQueueItems(dedupeHeartbeatInBuffer(items));
}

export function recoverCorruptedReplaySnapshot(): void {
  _testClearReplaySnapshots();
}

export function recoverReplayOwnershipMismatch(): boolean {
  const session = getActiveReplaySession();
  if (!session) return false;
  if (assertReplaySessionValid()) return false;
  failReplaySession();
  scheduleReplayRecovery();
  return true;
}

export function _testClearRecoverySnapshot(): void {
  recoverySnapshot = null;
}
